import { z } from 'zod';

/**
 * Обязательства по договору с графиком на год вперёд (ДГ-20), обеспечения и банковские гарантии (ДГ-10, ФН-07).
 * Просроченное и ближайшие 30 дней видны на главной («Ждёт меня»), ближайшая неделя — в утренней сводке.
 */

export const OBLIGATION_KINDS = {
  payment: 'Платёж',
  delivery: 'Поставка',
  work: 'Работы или этап',
  document: 'Документы',
  notice: 'Уведомление',
  renewal: 'Продление или возобновление',
  other: 'Другое',
} as const;
export type ObligationKind = keyof typeof OBLIGATION_KINDS;
export const OBLIGATION_KIND_CODES = Object.keys(OBLIGATION_KINDS) as [ObligationKind, ...ObligationKind[]];

/** События, после которых начинается отсчёт срока уведомления (ДГ-20) */
export const NOTICE_EVENTS = {
  delay: 'Задержка',
  extra_work: 'Дополнительные работы',
  claim: 'Претензия',
  suspension: 'Приостановка',
  instruction: 'Предписание или указание',
  other: 'Другое событие',
} as const;
export type NoticeEvent = keyof typeof NOTICE_EVENTS;
export const NOTICE_EVENT_CODES = Object.keys(NOTICE_EVENTS) as [NoticeEvent, ...NoticeEvent[]];

export const OBLIGATION_STATUSES = {
  waiting: 'Ждёт события',
  open: 'В работе',
  done: 'Выполнено',
  cancelled: 'Отменено',
} as const;
export type ObligationStatus = keyof typeof OBLIGATION_STATUSES;

export const SECURITY_KINDS = {
  tender: 'Тендерная гарантия',
  performance: 'Гарантия исполнения',
  advance: 'Гарантия возврата аванса',
  warranty: 'Гарантия гарантийных обязательств',
  letter_of_credit: 'Аккредитив',
  warranty_period: 'Гарантийный срок по объекту',
} as const;
export type SecurityKind = keyof typeof SECURITY_KINDS;
export const SECURITY_KIND_CODES = Object.keys(SECURITY_KINDS) as [SecurityKind, ...SecurityKind[]];

/** Окно «Ждёт меня» на главной и в сводке, дней */
export const OBLIGATION_HOME_DAYS = 30;
export const OBLIGATION_DIGEST_DAYS = 7;
/** График по умолчанию — на год вперёд, месяцев */
export const OBLIGATION_SCHEDULE_MONTHS = 12;

const Day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(
    (d) =>
      Number.isFinite(Date.parse(`${d}T00:00:00Z`)) && new Date(`${d}T00:00:00Z`).toISOString().startsWith(d),
    'Неверная дата',
  );

export const ObligationInput = z
  .object({
    kind: z.enum(OBLIGATION_KIND_CODES),
    title: z.string().trim().min(2).max(300),
    /** Срок; для уведомления, которое ждёт события, не нужен */
    dueOn: Day.nullish(),
    responsibleId: z.uuid().nullish(),
    note: z.string().trim().max(2000).nullish(),
    /** Уведомление: событие и число дней на извещение (например, 28 по FIDIC 2017, п. 20.2.1) */
    triggerEvent: z.enum(NOTICE_EVENT_CODES).nullish(),
    noticeDays: z.number().int().min(1).max(365).nullish(),
    /** Повтор: каждые N месяцев, всего M раз (серия на год вперёд) */
    repeatMonths: z.number().int().min(1).max(12).nullish(),
    repeatCount: z.number().int().min(2).max(36).nullish(),
  })
  .refine((v) => (v.kind === 'notice' && v.noticeDays ? true : !!v.dueOn), {
    message: 'Укажите срок',
    path: ['dueOn'],
  })
  .refine((v) => !(v.repeatMonths && !v.repeatCount) && !(v.repeatCount && !v.repeatMonths), {
    message: 'Повтор задают вместе: через сколько месяцев и сколько раз',
    path: ['repeatCount'],
  })
  .refine((v) => !(v.repeatMonths && !v.dueOn), { message: 'Для повтора нужен первый срок', path: ['dueOn'] })
  .refine((v) => !(v.noticeDays && !v.triggerEvent), {
    message: 'Для срока уведомления укажите событие',
    path: ['triggerEvent'],
  })
  .refine((v) => !(v.triggerEvent && !v.noticeDays), {
    message: 'Для уведомления укажите число дней',
    path: ['noticeDays'],
  });
export type ObligationInput = z.infer<typeof ObligationInput>;

export const ObligationPatch = z.object({
  title: z.string().trim().min(2).max(300).optional(),
  dueOn: Day.optional(),
  responsibleId: z.uuid().nullish(),
  note: z.string().trim().max(2000).nullish(),
});
export type ObligationPatch = z.infer<typeof ObligationPatch>;

export const ObligationStart = z.object({ startedOn: Day.optional() });
export const ObligationDone = z.object({ doneOn: Day.optional() });
export const ObligationEvent = z.object({ event: z.enum(NOTICE_EVENT_CODES), happenedOn: Day.optional() });

export const SecurityInput = z
  .object({
    kind: z.enum(SECURITY_KIND_CODES),
    bank: z.string().trim().max(200).nullish(),
    number: z.string().trim().max(100).nullish(),
    amount: z.number().nonnegative().max(1e13).nullish(),
    currency: z.string().trim().min(3).max(8).default('KZT'),
    commission: z.number().nonnegative().max(1e12).nullish(),
    issuedOn: Day.nullish(),
    validUntil: Day,
    remindDays: z.number().int().min(1).max(365).default(30),
    responsibleId: z.uuid().nullish(),
    note: z.string().trim().max(2000).nullish(),
  })
  .refine((v) => !v.issuedOn || v.issuedOn <= v.validUntil, {
    message: 'Срок окончания раньше даты выдачи',
    path: ['validUntil'],
  });
export type SecurityInput = z.infer<typeof SecurityInput>;

export const SecurityExtend = z.object({
  validUntil: Day,
  number: z.string().trim().max(100).nullish(),
  commission: z.number().nonnegative().max(1e12).nullish(),
});
export const SecurityReturn = z.object({ returnedOn: Day.optional() });

export const ObligationRow = z.object({
  id: z.uuid(),
  contractId: z.uuid(),
  contractNumber: z.string(),
  projectId: z.uuid(),
  projectCode: z.string(),
  direction: z.string(),
  counterpartyName: z.string(),
  kind: z.string(),
  title: z.string(),
  dueOn: z.string().nullable(),
  status: z.string(),
  responsibleId: z.uuid().nullable(),
  responsibleName: z.string().nullable(),
  triggerEvent: z.string().nullable(),
  noticeDays: z.number().nullable(),
  startedOn: z.string().nullable(),
  doneOn: z.string().nullable(),
  seriesId: z.uuid().nullable(),
  note: z.string().nullable(),
  overdue: z.boolean(),
  /** Дней до срока; отрицательное — просрочено */
  daysLeft: z.number().nullable(),
});
export type ObligationRow = z.infer<typeof ObligationRow>;

export const SecurityRow = z.object({
  id: z.uuid(),
  contractId: z.uuid(),
  contractNumber: z.string(),
  projectId: z.uuid(),
  projectCode: z.string(),
  counterpartyName: z.string(),
  kind: z.string(),
  bank: z.string().nullable(),
  number: z.string().nullable(),
  /** Без уровня денег «Акты и счета» суммы скрыты */
  amount: z.number().nullable(),
  commission: z.number().nullable(),
  currency: z.string(),
  issuedOn: z.string().nullable(),
  validUntil: z.string(),
  remindDays: z.number(),
  status: z.string(),
  returnedOn: z.string().nullable(),
  responsibleId: z.uuid().nullable(),
  responsibleName: z.string().nullable(),
  note: z.string().nullable(),
  overdue: z.boolean(),
  /** Договор исполнен или расторгнут, а обеспечение не возвращено */
  toReturn: z.boolean(),
  daysLeft: z.number(),
});
export type SecurityRow = z.infer<typeof SecurityRow>;

export const ObligationSchedule = z.object({
  from: z.string(),
  to: z.string(),
  obligations: z.array(ObligationRow),
  securities: z.array(SecurityRow),
  months: z.array(z.object({ month: z.string(), obligations: z.number(), securities: z.number() })),
  overdue: z.number(),
  next30: z.number(),
  moneyHidden: z.boolean(),
});
export type ObligationSchedule = z.infer<typeof ObligationSchedule>;

export const ContractObligations = z.object({
  obligations: z.array(ObligationRow),
  securities: z.array(SecurityRow),
  moneyHidden: z.boolean(),
  /** Кого можно назначить ответственным: действующие сотрудники компании */
  people: z.array(z.object({ id: z.uuid(), fullName: z.string() })),
});
export type ContractObligations = z.infer<typeof ContractObligations>;
